import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { Swipeable } from 'react-swipeable';
import { classNames, liveAnnounce, Level } from 'uitk-react-utils';
import { UitkOverlayButton, UitkPagingPrevButton, UitkPagingNextButton, UitkButtonTheme } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';
import { UitkGalleryCarouselItem } from './uitk-gallery-carousel-item.esm.js';
import styles from './uitk-gallery-carousel.css';

var getClassNameList = classNames.bind(styles);
var UitkHeadingTagMap = [
    ['h1', 'h1'],
    ['h2', 'h2'],
    ['h3', 'h3'],
    ['h4', 'h4'],
    ['h5', 'h5'],
    ['h6', 'h6']
];
var defaultHeadingTag = 'h3';
var UitkHeadingTags = new Map(UitkHeadingTagMap);
var UitkGalleryCarouselLocalAnalyticsIdType;
(function (UitkGalleryCarouselLocalAnalyticsIdType) {
    UitkGalleryCarouselLocalAnalyticsIdType["clickLeft"] = "clickLeft";
    UitkGalleryCarouselLocalAnalyticsIdType["clickRight"] = "clickRight";
})(UitkGalleryCarouselLocalAnalyticsIdType || (UitkGalleryCarouselLocalAnalyticsIdType = {}));
var GalleryCarousel = /** @class */ (function (_super) {
    __extends(GalleryCarousel, _super);
    function GalleryCarousel(props) {
        var _this = _super.call(this, props) || this;
        _this.nextprevCallbackTimer = 250;
        _this.state = {
            currentIndex: props.currentIndex || 0,
            enablePreload: Boolean(props.enablePreload),
            isInteractive: false
        };
        _this.onImageClickCallback = _this.onImageClickCallback.bind(_this);
        _this.onNextCallback = _this.onNextCallback.bind(_this);
        _this.onPrevCallback = _this.onPrevCallback.bind(_this);
        _this.onUpdateCallback = _this.onUpdateCallback.bind(_this);
        _this.onMediaButtonCallback = _this.onMediaButtonCallback.bind(_this);
        _this.onVirtualTourCallback = _this.onVirtualTourCallback.bind(_this);
        _this.getBackwardIndex = _this.getBackwardIndex.bind(_this);
        _this.getForwardIndex = _this.getForwardIndex.bind(_this);
        _this.onSwiping = _this.onSwiping.bind(_this);
        return _this;
    }
    GalleryCarousel.prototype.componentDidMount = function () {
        this.setState({
            isAnimating: false,
            isInteractive: true
        });
    };
    GalleryCarousel.prototype.UNSAFE_componentWillReceiveProps = function (nextProps) {
        var nextCurrIndex = nextProps.currentIndex;
        var currentIndex = this.state.currentIndex;
        if (nextCurrIndex !== currentIndex) {
            this.setState({ currentIndex: nextCurrIndex });
        }
    };
    GalleryCarousel.prototype.shouldComponentUpdate = function (nextProps, nextState) {
        var _a = this.props, currentIndex = _a.currentIndex, images = _a.images;
        var isAnimating = this.state.isAnimating;
        var nextImages = nextProps.images;
        // Only re-render when images, isAnimating or currentIndex change
        var imageLength = images && images.length;
        var nextImageLength = nextImages && nextImages.length;
        if (imageLength !== nextImageLength) {
            return true;
        }
        if (isAnimating !== nextState.isAnimating) {
            return true;
        }
        if (currentIndex !== nextProps.currentIndex) {
            return true;
        }
        if (imageLength > 0 && nextImageLength > 0 && images[0].src !== nextImages[0].src) {
            return true;
        }
        return false;
    };
    GalleryCarousel.prototype.onPrevCallback = function () {
        var _this = this;
        var isAnimating = this.state.isAnimating;
        var images = this.props.images;
        if (isAnimating) {
            return;
        }
        var newIndex = this.currentIndex - 1;
        var imagesLength = images.length;
        if (newIndex < 0) {
            newIndex = imagesLength - 1;
        }
        this.setState({
            enablePreload: true,
            isAnimating: true,
            isAnimatingNext: false,
            isAnimatingPrev: true,
            isInteractive: false
        });
        if (this._animationTimer) {
            clearInterval(this._animationTimer);
        }
        this._animationTimer = setTimeout(function () {
            _this.announce(newIndex);
            _this.onUpdateCallback(newIndex);
        }, this.nextprevCallbackTimer);
    };
    GalleryCarousel.prototype.onNextCallback = function () {
        var _this = this;
        var isAnimating = this.state.isAnimating;
        var images = this.props.images;
        if (isAnimating) {
            return;
        }
        var newIndex = this.currentIndex + 1;
        var imagesLength = images.length;
        if (newIndex + 1 > imagesLength) {
            newIndex = 0;
        }
        this.setState({
            enablePreload: true,
            isAnimating: true,
            isAnimatingNext: true,
            isAnimatingPrev: false,
            isInteractive: false
        });
        if (this._animationTimer) {
            clearInterval(this._animationTimer);
        }
        this._animationTimer = setTimeout(function () {
            _this.announce(newIndex);
            _this.onUpdateCallback(newIndex);
        }, this.nextprevCallbackTimer);
    };
    GalleryCarousel.prototype.onUpdateCallback = function (currentIndex) {
        var onIndexChange = this.props.onIndexChange;
        if (typeof onIndexChange === 'function') {
            onIndexChange(currentIndex);
        }
    };
    GalleryCarousel.prototype.onImageClickCallback = function () {
        var onImageClick = this.props.onImageClick;
        if (typeof onImageClick === 'function') {
            onImageClick();
        }
    };
    GalleryCarousel.prototype.onMediaButtonCallback = function () {
        var onMediaButtonClick = this.props.onMediaButtonClick;
        if (typeof onMediaButtonClick === 'function') {
            onMediaButtonClick(this.currentIndex + 1);
        }
    };
    GalleryCarousel.prototype.onVirtualTourCallback = function () {
        var onVirtualTourClick = this.props.onVirtualTourClick;
        if (typeof onVirtualTourClick === 'function') {
            onVirtualTourClick();
        }
    };
    GalleryCarousel.prototype.onSwiping = function (event) {
        var images = this.props.images;
        var isInteractive = this.state.isInteractive;
        var imagesLength = images.length;
        var canSwipe = isInteractive && images && imagesLength > 1;
        if (canSwipe && event.dir === 'Left') {
            this.onNextCallback();
        }
        if (canSwipe && event.dir === 'Right') {
            this.onPrevCallback();
        }
    };
    GalleryCarousel.prototype.getBackwardIndex = function (count) {
        var images = this.props.images;
        if (images && images.length > 0 && count > 0) {
            if (images.length === 1) {
                return 0;
            }
            var softIndex = this.currentIndex - count;
            if (softIndex >= 0) {
                return softIndex;
            }
            if (Math.abs(softIndex % images.length) === 0) {
                return 0;
            }
            return Math.abs(images.length - Math.abs(softIndex % images.length));
        }
        return 0;
    };
    GalleryCarousel.prototype.getForwardIndex = function (count) {
        var images = this.props.images;
        if (images && images.length > 0 && count > 0) {
            var softIndex = this.currentIndex + count;
            return Math.abs(softIndex % images.length);
        }
        return this.currentIndex;
    };
    Object.defineProperty(GalleryCarousel.prototype, "currentIndex", {
        // Getter / setter for this.currentIndex
        get: function () {
            var currentIndex = this.state.currentIndex;
            return currentIndex;
        },
        set: function (value) {
            this.setState({ currentIndex: value });
        },
        enumerable: false,
        configurable: true
    });
    GalleryCarousel.prototype.stopTransitionPropagation = function (e) {
        e.stopPropagation();
    };
    GalleryCarousel.prototype.announce = function (index) {
        var _a = this.props, images = _a.images, l10n = _a.l10n;
        var currentIndex = this.state.currentIndex;
        var captionToBeRead;
        if (images[index].caption !== undefined) {
            captionToBeRead = images[index].caption;
        }
        else if (images[index].alt === undefined) {
            captionToBeRead = '';
        }
        else {
            captionToBeRead = images[index].alt;
        }
        this.setState({
            currentIndex: index,
            isAnimating: false,
            isAnimatingNext: false,
            isAnimatingPrev: false,
            isInteractive: true
        }, function () {
            return liveAnnounce(l10n.formatText('gallerycarousel.imageChange', currentIndex + 1, images.length, captionToBeRead), Level.POLITE);
        });
    };
    // Make allyHeadingLevel a valid tag if its type is 'number'
    // Defaults to h3 if number is not a valid level
    GalleryCarousel.prototype.makeValidTag = function (level) {
        if (typeof level === 'number') {
            if (level < 1 || level > 6 || !Number.isInteger(level)) {
                return defaultHeadingTag;
            }
            return "h".concat(level);
        }
        return level;
    };
    GalleryCarousel.prototype.render = function () {
        var _a;
        var _b = this.props, allyHeadingLevel = _b.allyHeadingLevel, allyHeadingText = _b.allyHeadingText, analytics = _b.analytics, buttonText = _b.buttonText, carouselName = _b.carouselName, carouselId = _b.carouselId, listingName = _b.listingName, className = _b.className, images = _b.images, l10n = _b.l10n, showGalleryButton = _b.showGalleryButton, galleryButtonRef = _b.galleryButtonRef, showScrim = _b.showScrim, showVirtualTourButton = _b.showVirtualTourButton, virtualTourButtonRef = _b.virtualTourButtonRef, imageTriggerRef = _b.imageTriggerRef, navTheme = _b.navTheme, ratio = _b.ratio, videoAnalytics = _b.videoAnalytics, showCaption = _b.showCaption, videoButtonText = _b.videoButtonText, placeholderImage = _b.placeholderImage, triggerA11yText = _b.triggerA11yText, fetchPriority = _b.fetchPriority;
        var _c = this.state, currentIndex = _c.currentIndex, isAnimating = _c.isAnimating, isAnimatingNext = _c.isAnimatingNext, isAnimatingPrev = _c.isAnimatingPrev, enablePreload = _c.enablePreload;
        var figureAnimationClass = '';
        var baseCssClass = getClassNameList('uitk-gallery-carousel', className);
        var imagesLength = images.length;
        var HeadingLevel = allyHeadingLevel
            ? UitkHeadingTags.get(this.makeValidTag(allyHeadingLevel))
            : UitkHeadingTags.get(defaultHeadingTag);
        if (isAnimating) {
            if (isAnimatingPrev) {
                figureAnimationClass = 'uitk-gallery-carousel-prev';
            }
            else if (isAnimatingNext) {
                figureAnimationClass = 'uitk-gallery-carousel-next';
            }
        }
        // Will send a track event with the current index when paginating backwards
        var clickLeftAnalytics = analytics && __assign({ localAnalyticsId: "".concat(carouselName, ".GalleryCarousel.").concat(UitkGalleryCarouselLocalAnalyticsIdType.clickLeft, ".").concat(currentIndex) }, analytics);
        // Will send a track event with the current index when paginating forwards
        var clickRightAnalytics = analytics && __assign({ localAnalyticsId: "".concat(carouselName, ".GalleryCarousel.").concat(UitkGalleryCarouselLocalAnalyticsIdType.clickRight, ".").concat(currentIndex) }, analytics);
        var clickMediaGalleryAnalytics = analytics && __assign({ localAnalyticsId: "".concat(carouselName, ".MediaGalleryTrigger") }, analytics);
        var buttonAndCaptionMarkup = (React.createElement("div", { className: getClassNameList('uitk-gallery-carousel-footer') },
            showCaption && (React.createElement("div", { className: getClassNameList('uitk-gallery-carousel-footer-media-caption') }, images[currentIndex].caption)),
            React.createElement("div", { className: getClassNameList('uitk-action-buttons') },
                showVirtualTourButton && (React.createElement(UitkOverlayButton, { className: getClassNameList('uitk-virtual-tour-button'), analytics: clickMediaGalleryAnalytics, onClick: this.onVirtualTourCallback, domRef: virtualTourButtonRef },
                    React.createElement(UitkIcon, { name: "tour_360", title: buttonText === null || buttonText === void 0 ? void 0 : buttonText.virtualTourButton, id: "tour_360-".concat(carouselId) }),
                    React.createElement("span", { "aria-hidden": "true" }, buttonText === null || buttonText === void 0 ? void 0 : buttonText.virtualTourButton))),
                showGalleryButton && (React.createElement(UitkOverlayButton, { className: getClassNameList('uitk-gallery-button'), analytics: clickMediaGalleryAnalytics, onClick: this.onMediaButtonCallback, domRef: galleryButtonRef },
                    React.createElement(UitkIcon, { name: "photo_library", title: (_a = buttonText === null || buttonText === void 0 ? void 0 : buttonText.galleryButton) !== null && _a !== void 0 ? _a : (listingName
                            ? l10n.formatText('gallerycarousel.mediaGalleryButton.withName', imagesLength, listingName)
                            : l10n.formatText('gallerycarousel.mediaGalleryButton', imagesLength)), id: "photo_library-".concat(carouselId) }),
                    React.createElement("span", { "aria-hidden": "true" }, String(imagesLength)))))));
        return (images &&
            imagesLength > 0 && (React.createElement("div", { className: baseCssClass, onTransitionEnd: this.stopTransitionPropagation },
            React.createElement(HeadingLevel, { className: getClassNameList('is-visually-hidden') }, allyHeadingText),
            React.createElement(Swipeable, { className: getClassNameList("uitk-gallery-carousel-items", figureAnimationClass, ratio), onSwiping: this.onSwiping, delta: 70 },
                React.createElement(UitkGalleryCarouselItem, __assign({}, images[this.getBackwardIndex(1)], { ariaHidden: true, className: getClassNameList('uitk-gallery-carousel-item-prev'), ratio: ratio, enablePreload: enablePreload, key: "".concat(carouselId).concat(this.currentIndex - 1), triggerRef: imageTriggerRef })),
                React.createElement(UitkGalleryCarouselItem, __assign({}, images[this.currentIndex], { className: getClassNameList('uitk-gallery-carousel-item-current'), ratio: ratio, enablePreload: true, onClick: this.onImageClickCallback, videoAnalytics: videoAnalytics, videoButtonText: videoButtonText, placeholderImage: placeholderImage, triggerA11yText: triggerA11yText, key: "".concat(carouselId).concat(this.currentIndex), fetchPriority: fetchPriority, triggerRef: imageTriggerRef })),
                React.createElement(UitkGalleryCarouselItem, __assign({}, images[this.getForwardIndex(1)], { ariaHidden: true, className: getClassNameList('uitk-gallery-carousel-item-next'), ratio: ratio, enablePreload: enablePreload, key: "".concat(carouselId).concat(this.currentIndex + 1), triggerRef: imageTriggerRef })),
                React.createElement(UitkGalleryCarouselItem, __assign({}, images[this.getForwardIndex(2)], { ariaHidden: true, className: getClassNameList('uitk-gallery-carousel-item-queued'), ratio: ratio, enablePreload: enablePreload, triggerRef: imageTriggerRef }))),
            imagesLength > 1 && (React.createElement("div", { className: getClassNameList('uitk-gallery-carousel-paging-controls') },
                React.createElement(UitkPagingPrevButton, { className: getClassNameList('uitk-gallery-carousel-button-prev'), iconId: "prev-button-".concat(carouselId), iconTitle: (buttonText === null || buttonText === void 0 ? void 0 : buttonText.prevButton) ? buttonText.prevButton : l10n.formatText('gallerycarousel.previousImage'), onClick: this.onPrevCallback, analytics: clickLeftAnalytics, theme: navTheme }),
                React.createElement(UitkPagingNextButton, { className: getClassNameList('uitk-gallery-carousel-button-next'), iconId: "next-button-".concat(carouselId), iconTitle: (buttonText === null || buttonText === void 0 ? void 0 : buttonText.nextButton) ? buttonText.nextButton : l10n.formatText('gallerycarousel.nextImage'), onClick: this.onNextCallback, analytics: clickRightAnalytics, theme: navTheme }))),
            showScrim && (React.createElement(UitkScrim, { className: getClassNameList('uitk-gallery-carousel-scrim'), tag: "div", type: UitkScrimType.BOTTOM }, (showCaption || showGalleryButton || showVirtualTourButton) && buttonAndCaptionMarkup)),
            !showScrim && (showCaption || showGalleryButton || showVirtualTourButton) && (React.createElement("div", { className: getClassNameList('uitk-gallery-carousel-box') }, buttonAndCaptionMarkup)))));
    };
    GalleryCarousel.defaultProps = {
        carouselId: 'carousel-navigation-id',
        currentIndex: 0,
        enablePreload: true,
        navTheme: UitkButtonTheme.SOLID,
        ratio: UitkFigureAspectRatioType.R16_9,
        showGalleryButton: true,
        showVirtualTourButton: false
    };
    return GalleryCarousel;
}(React.Component));
var UitkGalleryCarousel = function (props) {
    var l10n = useLocalization();
    return React.createElement(GalleryCarousel, __assign({ l10n: l10n }, props));
};

export { UitkGalleryCarousel, UitkGalleryCarouselLocalAnalyticsIdType };
